/* Native links + native details, enhanced with hover and editor support.
   Concept continues to own search/cart/account and the mobile drawer. */
(() => {
  if (customElements.get('pp-navigation')) return;
  class PPNavigation extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.timers = new Set();
      const on = (node, event, fn, options = {}) => node?.addEventListener(event, fn, { ...options, signal: this.controller.signal });
      this.desktop = this.dataset.mode === 'desktop';
      this.header = this.closest('.pp-header');
      this.drawer = this.closest('.pp-menu-drawer');
      this.disclosures = [...this.querySelectorAll('.pp-nav-disclosure')];
      this.serviceDisclosures = [...this.querySelectorAll('.pp-nav-service-disclosure')];
      this.allDisclosures = [...this.disclosures, ...this.serviceDisclosures];
      this.breakpoint = Number(this.dataset.breakpoint) || 1184;
      this.media = matchMedia(`(min-width: ${this.breakpoint}px)`);
      this.hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
      this.hover = this.dataset.hover === 'true';
      this.delay = Number(this.dataset.delay) || 0;
      this.closeDelay = Number(this.dataset.closeDelay) || 340;
      this.disclosures.forEach(details => {
        const summary = details.querySelector(':scope > summary');
        summary.setAttribute('aria-expanded', String(details.open));
        on(details, 'toggle', () => {
          if (details.open) {
            this.disclosures.forEach(other => { if (other !== details) other.open = false; });
            this.placePanel();
          }
          summary.setAttribute('aria-expanded', String(details.open));
          this.syncHeader();
        });
        on(summary, 'click', () => this.clearTimers());
        on(summary, 'keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault(); this.open(details);
            details.querySelector('.pp-nav-panel a')?.focus();
          }
        });
        const item = details.closest('.pp-nav-item');
        on(item, 'pointerenter', event => {
          if (!this.desktop || !this.hover || !this.hoverMedia.matches || event.pointerType === 'touch' || this.editorPinned) return;
          this.clearTimers();
          this.schedule(() => this.open(details), this.delay);
        });
        on(details.querySelector('.pp-nav-panel'), 'pointerenter', () => { if (this.desktop && details.open) this.clearTimers(); });
        on(item, 'pointerleave', () => {
          if (!this.desktop || !this.hover || !this.hoverMedia.matches || this.editorPinned) return;
          this.clearTimers();
          this.schedule(() => { if (!item.contains(document.activeElement)) details.open = false; }, this.closeDelay);
        });
      });
      this.serviceDisclosures.forEach(details => {
        const summary = details.querySelector(':scope > summary');
        summary.setAttribute('aria-expanded', String(details.open));
        on(details, 'toggle', () => {
          summary.setAttribute('aria-expanded', String(details.open));
          if (details.open) this.serviceDisclosures.forEach(other => { if (other !== details) other.open = false; });
        });
      });
      on(this, 'keydown', event => {
        if (event.key === 'Escape') {
          const open = event.target.closest('details[open]') || this.allDisclosures.find(d => d.open);
          if (open) { event.preventDefault(); event.stopPropagation(); this.escapeHandled = true; open.open = false; open.querySelector('summary')?.focus(); }
        }
      });
      on(this, 'keyup', event => {
        if (event.key === 'Escape' && this.escapeHandled) { event.stopPropagation(); this.escapeHandled = false; }
      });
      on(document, 'pointerdown', event => {
        if (this.desktop && !this.contains(event.target) && !this.editorPinned) this.closeAll();
      });
      on(this, 'focusout', () => this.schedule(() => {
        if (this.desktop && !this.contains(document.activeElement) && !this.matches(':hover') && !this.editorPinned) this.closeAll();
      }, 0));
      on(this, 'click', event => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        this.closeAll();
        if (this.drawer?.open) {
          // An app opened by a hash link owns the next focus target.
          if (link.getAttribute('href').startsWith('#')) this.drawer.activeElement = null;
          this.drawer.hide();
        }
      });
      on(document, 'drawer:afterShow', event => {
        if (this.desktop) this.closeAll();
      });
      on(this.drawer, 'drawer:afterHide', () => this.closeAll());
      on(document, 'shopify:block:select', event => this.selectBlock(event));
      on(document, 'shopify:block:deselect', () => { this.editorPinned = false; this.closeAll(); });
      on(document, 'shopify:section:unload', event => {
        if (event.detail?.sectionId === this.dataset.section && this.drawer) {
          const drawer = this.drawer;
          if (drawer.parentElement === document.body) {
            // Finish Concept's transition so its internal scroll-lock counter
            // is released. Never append the old drawer to a re-rendered header.
            drawer.originalParentBeforeAppend = null;
            drawer.activeElement = null;
            drawer.id = `PPMenuClosing-${this.dataset.section}`;
            Promise.resolve(drawer.hide()).then(() => {
              drawer.remove();
              if (document.querySelector('.drawer[open]')) document.body.classList.add('has-modal-open');
            });
          } else drawer.hide();
        }
      });
      on(this.media, 'change', () => { this.closeAll(); this.measure(); if (this.drawer?.open && this.media.matches) this.drawer.hide(); });
      on(window, 'resize', () => { this.placePanel(); this.measure(); }, { passive: true });
      if (this.header) {
        this.resizeObserver = new ResizeObserver(() => { this.placePanel(); this.measure(); });
        this.resizeObserver.observe(this.header);
        this.resizeObserver.observe(this.querySelector('.pp-nav-primary'));
        document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
        this.measure();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.clearTimers();
      this.header?.removeAttribute('data-pp-open');
    }
    schedule(fn, delay) {
      const id = setTimeout(() => { this.timers.delete(id); fn(); }, delay);
      this.timers.add(id);
    }
    clearTimers() { this.timers?.forEach(clearTimeout); this.timers?.clear(); }
    open(details) {
      this.clearTimers();
      this.disclosures.forEach(other => { other.open = other === details; });
      this.placePanel(); this.syncHeader();
    }
    closeAll() { this.clearTimers(); this.allDisclosures.forEach(d => d.open = false); this.syncHeader(); }
    syncHeader() { this.header?.toggleAttribute('data-pp-open', this.disclosures.some(d => d.open)); }
    placePanel() {
      if (!this.header) return;
      const header = this.header.getBoundingClientRect();
      this.header.style.setProperty('--pp-nav-top', `${Math.max(0, header.bottom)}px`);
      const gutter = 16;
      this.disclosures.filter(details => details.open).forEach(details => {
        const panel = details.querySelector('.pp-nav-panel');
        const trigger = details.closest('.pp-nav-item').getBoundingClientRect();
        const width = panel.getBoundingClientRect().width;
        const desired = panel.classList.contains('pp-nav-panel--services') ? trigger.right - width : trigger.left;
        const left = Math.max(gutter, Math.min(desired, document.documentElement.clientWidth - width - gutter));
        panel.style.setProperty('--pp-nav-panel-left', `${left - header.left}px`);
      });
    }
    measure() {
      if (!this.header || this.measuring) return;
      this.measuring = true;
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        if (!this.media.matches) this.header.removeAttribute('data-pp-overflow');
        else {
          const row = this.querySelector('.pp-nav-primary');
          const style = getComputedStyle(this.header);
          const logo = this.header.querySelector('.header__logo')?.getBoundingClientRect().width || 150;
          const icons = this.header.querySelector('.header__icons--end .header__buttons')?.getBoundingClientRect().width || 140;
          const space = this.header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - logo - icons - 2 * (parseFloat(style.columnGap) || 16);
          this.header.toggleAttribute('data-pp-overflow', row.scrollWidth > space + 1);
          if (this.header.hasAttribute('data-pp-overflow')) this.closeAll();
        }
        this.measuring = false;
      });
    }
    selectBlock(event) {
      if (event.detail?.sectionId !== this.dataset.section) return;
      const block = [...this.querySelectorAll('[data-pp-block]')].find(el => el.dataset.ppBlock === event.detail.blockId);
      if (!block) return;
      const useDrawer = !this.media.matches || document.querySelector('.pp-header[data-pp-overflow]');
      if (this.desktop === Boolean(useDrawer)) return;
      this.editorPinned = true;
      const details = block.closest('.pp-nav-disclosure') || block.querySelector('.pp-nav-disclosure');
      if (details) this.open(details);
      if (this.drawer) this.drawer.show();
      this.schedule(() => block.scrollIntoView({ block: 'nearest' }), 300);
    }
  }
  customElements.define('pp-navigation', PPNavigation);
})();
